import { useMemo } from 'react';
import { useRouter } from 'next/router';

import { Box, Flex } from '@chakra-ui/react';

import { getChakraReactSelectStyles, SelectInput } from '~/components/select-input';
import { trackEvent } from '~/features/analytics/track-event';
import { DEFAULT_SORT } from '~/features/catalog/models/filter';
import { updateCatalogUrlState } from '~/features/catalog/utils/catalog-url-state-utils';
import { getSortOptions } from '~/features/catalog/utils/get-sort-options';
import { SortIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';
import { useClientRender } from '~/hooks/use-client-render';
import { useI18n } from '~/hooks/use-i18n';
import { isSelectOption } from '~/types/select-option';

const sortSelectStyles = {
  ...getChakraReactSelectStyles('sm', { controlBorderColor: 'grey.500' }),
  container: (provided: Record<string, unknown>) => ({
    ...provided,
    width: '160px',
  }),
  control: (provided: Record<string, unknown>) => ({
    ...provided,
    bg: 'white',
    borderColor: 'grey.500',
    borderRadius: '4px',
    boxShadow: 'none',
    height: '38px',
    minHeight: '38px',
    minWidth: '160px',
    width: '100%',
    _hover: {
      borderColor: 'grey.500',
    },
    _focusVisible: {
      borderColor: 'grey.500',
      boxShadow: 'none',
    },
  }),
  valueContainer: (provided: Record<string, unknown>) => ({
    ...provided,
    color: 'grey.900',
    fontSize: '0.8125rem',
    fontWeight: 500,
    height: '100%',
    lineHeight: '18px',
    padding: '0 0 0 16px',
    textAlign: 'left',
  }),
  placeholder: (provided: Record<string, unknown>) => ({
    ...provided,
    color: 'grey.900',
    fontSize: '0.8125rem',
    fontWeight: 500,
    lineHeight: '18px',
    margin: 0,
    whiteSpace: 'nowrap',
  }),
  dropdownIndicator: (
    provided: Record<string, unknown>,
    { selectProps }: { selectProps: { menuIsOpen?: boolean } }
  ) => ({
    ...provided,
    background: 'transparent',
    border: 'none',
    color: 'grey.300',
    paddingEnd: '14px',
    paddingStart: '16px',
    w: 'auto',
    '> svg': {
      h: '14px',
      transform: `rotate(${selectProps.menuIsOpen ? 180 : 0}deg)`,
      transition: '300ms ease all',
      w: '14px',
    },
  }),
  option: (
    provided: Record<string, unknown>,
    { isFocused, isSelected }: { isFocused: boolean; isSelected: boolean }
  ) => ({
    ...provided,
    bgColor: isFocused || isSelected ? 'grey.50' : 'white',
    borderLeft: 'none',
    color: isSelected ? 'grey.900' : 'grey.500',
    fontSize: '0.8125rem',
    fontWeight: 500,
    lineHeight: '18px',
    minHeight: '38px',
    padding: '10px 16px',
    _hover: {
      bgColor: 'grey.50',
      shadow: 'none',
    },
  }),
  menu: (provided: Record<string, unknown>) => ({
    ...provided,
    overflow: 'visible',
    width: '160px',
    zIndex: 3,
  }),
  menuList: (provided: Record<string, unknown>) => ({
    ...provided,
    borderColor: 'grey.200',
    p: 0,
    rounded: '4px',
    shadow: 'base',
  }),
};

export function CatalogSearchSort() {
  const isClient = useClientRender();
  const router = useRouter();
  const { t } = useI18n();
  const searchValue = router.query.searchValue;

  const { selectedSortOption, sortOptions } = useMemo(() => {
    const sortOptions = getSortOptions(!!searchValue, t);
    const defaultSortOption = sortOptions.find((sortOption) => sortOption.value === DEFAULT_SORT);

    const { sort: sortParam } = router.query;

    return {
      selectedSortOption: sortOptions.find((sortOption) => sortOption.value === sortParam) ?? defaultSortOption,
      sortOptions,
    };
  }, [router.query, searchValue, t]);

  function handleSortChange(selectedOption: unknown) {
    if (isSelectOption(selectedOption)) {
      trackEvent({
        type: 'click',
        name: 'catalogResultSorted',
        label: selectedOption.label,
        action: 'results_sorted',
      });

      updateCatalogUrlState({
        router,
        updatedRouteParameters: {
          sort: selectedOption.value,
        },
      });
    }
  }

  return (
    <Flex alignItems='center' as='label' flexShrink={0} gap='8px'>
      <Flex alignItems='center' color='grey.500' flexShrink={0} fontSize='0.75rem' gap='6px' lineHeight='18px'>
        <SortIcon height='16px' width='16px' />
        {t('catalog.sort.label')}
      </Flex>
      <Box
        flexShrink={0}
        w='160px'
        sx={{
          '.react-select': {
            width: '160px',
          },
          '.react-select > div:first-of-type': {
            height: '38px',
            minHeight: '38px',
            width: '160px',
          },
        }}
      >
        <SelectInput
          key={selectedSortOption?.value}
          isVirtual={false}
          name='catalogSearchSort'
          options={sortOptions}
          placeholder={selectedSortOption ? selectedSortOption.label ?? '' : ''}
          selectProps={{
            chakraStyles: sortSelectStyles,
            isSearchable: false,
            menuPortalTarget: isClient ? document.body : undefined,
            size: 'sm',
            value: '',
            onChange: handleSortChange,
          }}
        />
      </Box>
    </Flex>
  );
}
